import { ButtonContainerFloating1, ButtonNormalLarge, getColor } from '@banksalad/bpl-web';
import { openWebView, WebViewType } from '@banksalad/js-banksalad';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';

import { useRouterWithQuery } from '@/page-src/prequalification/pages/loans/hooks/use-router-with-query';
import { formatMsToSeoulDate } from '@/page-src/prequalification/pages/loans/utils';
import { DESKTOP_WIDTH } from '@/page-src/prequalification/pages/loans/utils/screen-resolution';
import { useGetDeposits } from '@/page-src/product/pages/deposits/hooks/use-get-deposits';
import { getDepositsMainInfoIssueUrlAndButtonText } from '@/page-src/product/pages/deposits/utils/get-main-info-issue-url-button-text';
import { getDepositsMainInfoToDisplay } from '@/page-src/product/pages/deposits/utils/get-main-info-to-display';
import { SAVINGS_MAIN_COLOR } from '@/src/@domain/savings/constants';
import { ProductDetailSummary } from '@/src/@domain/shopping/components/summary';
import { useIsWebViewWithSsr } from '@/src/contexts/use-is-webview-with-ssr';
import { trackExposeViewFinancialProductSearchDetail } from '@/src/events/trackers/product/deposits/product-guid';
import { openIssueUrlDeepLink, WEB_BASE_URL } from '@/src/utils/link';

export const ProductDepositMainInfoSection = () => {
  const { result, isValidProduct } = useGetDeposits();
  const { deposit, appliedCondition } = result;
  const isWebViewWithSsr = useIsWebViewWithSsr();
  const { routerPushWithQuery } = useRouterWithQuery();
  const { ref, inView: isApplyButtonExposed } = useInView({
    threshold: 1,
    initialInView: true,
  });

  useEffect(() => {
    trackExposeViewFinancialProductSearchDetail(deposit);
  }, []);

  const { text, urlPath, clickTracker } = getDepositsMainInfoIssueUrlAndButtonText(result);

  const isClickable = !isValidProduct || Boolean(deposit.issueUrl);

  const onClickButton = (location: 'floating' | 'fixed') => {
    if (urlPath === undefined) return;
    clickTracker(location);

    if (isWebViewWithSsr) {
      if (urlPath.includes(WEB_BASE_URL)) {
        openWebView({
          url: urlPath,
          type: WebViewType.WEB_VIEW_TYPE_SCREEN_FROM_RIGHT_TO_LEFT,
          nativeHeaderTitle: '적금',
        });
      } else {
        openIssueUrlDeepLink({
          title: '',
          url: urlPath,
        });
      }
    } else if (urlPath.includes(WEB_BASE_URL)) {
      routerPushWithQuery(urlPath);
    } else {
      window.open(urlPath, '_blank');
    }
  };

  return (
    <>
      <ProductDetailSummary
        ref={ref}
        imageUrl={deposit.organization.logoImageUrl}
        organizationName={deposit.organization.name}
        productName={deposit.name}
        isValidProduct={isValidProduct}
        leftSubtext="최고 금리"
        leftText={getDepositsMainInfoToDisplay(result).maxInterestRate}
        leftTextColor={SAVINGS_MAIN_COLOR.DEPOSIT}
        rightSubtext={`기본 금리(${appliedCondition.interestRateCondition.registrationMonths}개월 기준)`}
        rightText={getDepositsMainInfoToDisplay(result).interestRate}
        isExposable={deposit.isExposable}
        tags={deposit.tags.map(tag => tag.textToDisplay)}
        buttonInfo={{
          text,
          onClick: onClickButton,
          isClickable,
        }}
        description={`금융감독원 ${formatMsToSeoulDate(
          deposit.publishedAtMs,
          'yy.MM.dd'
        )}일 공시된 내용 기반`}
      />
      <FixedButtonWrapper isButtonExpose={!isApplyButtonExposed}>
        <ButtonContainerFloating1
          containerStyle={css`
            background-color: ${getColor('white-900')};
            max-width: ${DESKTOP_WIDTH}px;
            padding-left: 20px;
            padding-right: 20px;
            left: auto;
          `}
        >
          <ButtonNormalLarge
            containerStyles={css`
              border-radius: 16px;
            `}
            onClick={() => {
              onClickButton('floating');
            }}
            text={text}
            fontWeight="bold"
            backgroundColor={isClickable ? 'green-100' : 'gray-700'}
            color={isClickable ? 'white-900' : 'gray-550'}
            disabled={!isClickable}
          />
        </ButtonContainerFloating1>
      </FixedButtonWrapper>
    </>
  );
};

// TODO: ButtonFloatingByInViewWrapper 사용을 고려해주세요.
const FixedButtonWrapper = styled.div<{ isButtonExpose: boolean }>`
  position: fixed;
  z-index: 10;
  display: ${props => (props.isButtonExpose ? 'block' : 'none')};
  padding-top: 18px;
  max-width: ${DESKTOP_WIDTH}px;
`;
